import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { PortalSection } from '../context/AuthContext';
import client from '../api/client';

interface ApiUser {
  id: string;
  name: string;
  email: string;
  firmId: string;
  firmName: string;
  status: 'pending' | 'approved' | 'rejected';
  emailVerified: boolean;
  role: 'super_admin' | 'admin' | 'user';
  agentAccess?: boolean;
  sectionAccess?: Partial<Record<PortalSection, boolean>>;
  createdAt: any;
  lastLogin: any;
}

const SECTION_LABELS: { section: PortalSection; label: string }[] = [
  { section: 'meetings', label: 'Meetings' },
  { section: 'bestPractices', label: 'Best Practice' },
  { section: 'aiTool', label: 'AI Assistant' },
  { section: 'regulatoryUpdates', label: 'Regulatory Updates' },
];

interface Firm {
  id: string;
  name: string;
}

const formatDate = (createdAt: any): string => {
  try {
    if (!createdAt) return '—';
    if (createdAt._seconds) {
      return new Date(createdAt._seconds * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return new Date(createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

export const AdminPage: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFirm, setFilterFirm] = useState('all');
  const [search, setSearch] = useState('');

  // Add User modal state
  const [showAddUser, setShowAddUser] = useState(false);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [addForm, setAddForm] = useState({ name: '', email: '', firmId: '', role: 'user' as 'admin' | 'user', password: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');

  // Access permissions modal state
  const [accessModalUserId, setAccessModalUserId] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await client.get('/admin/users');
        setUsers(response.data.users || []);
      } catch (err: any) {
        setError(err.message || 'Error loading users');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchUsers();
    }
  }, [authLoading]);

  // Fetch firms when modal opens
  useEffect(() => {
    if (showAddUser && firms.length === 0) {
      client.get('/firms').then(r => setFirms(r.data.firms || [])).catch(() => {});
    }
  }, [showAddUser]);

  const firmNames = Array.from(new Set(users.map(u => u.firmName))).sort((a, b) => a.localeCompare(b));

  const filteredUsers = users
    .filter(u => {
      const matchesStatus = filterStatus === 'all' || u.status === filterStatus;
      const matchesFirm = filterFirm === 'all' || u.firmName === filterFirm;
      const matchesSearch = u.email.toLowerCase().includes(search.toLowerCase()) ||
                            u.name.toLowerCase().includes(search.toLowerCase()) ||
                            u.firmName.toLowerCase().includes(search.toLowerCase());
      return matchesStatus && matchesFirm && matchesSearch;
    })
    .sort((a, b) => {
      const aTime = a.createdAt?._seconds ?? 0;
      const bTime = b.createdAt?._seconds ?? 0;
      return bTime - aTime;
    });

  const handleApprove = async (userId: string) => {
    try {
      await client.post(`/admin/approve/${userId}`);
      setUsers(users.map(u => u.id === userId ? { ...u, status: 'approved' } : u));
    } catch (err: any) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleManualApprove = async (userId: string) => {
    if (!window.confirm('Verify this user’s email and approve them without email verification? Use this only when the email address is correct.')) return;
    try {
      await client.post(`/admin/manual-approve/${userId}`);
      setUsers(users.map(u => u.id === userId ? { ...u, emailVerified: true, status: 'approved' } : u));
    } catch (err: any) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleReject = async (userId: string) => {
    try {
      await client.post(`/admin/reject/${userId}`);
      setUsers(users.map(u => u.id === userId ? { ...u, status: 'rejected' } : u));
    } catch (err: any) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleRevoke = async (userId: string) => {
    try {
      await client.post(`/admin/revoke/${userId}`);
      setUsers(users.map(u => u.id === userId ? { ...u, status: 'rejected' } : u));
    } catch (err: any) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleResetEmailVerification = async (userId: string) => {
    try {
      await client.post(`/admin/reset-verification/${userId}`);
      setUsers(users.map(u => u.id === userId ? { ...u, emailVerified: false, status: u.status === 'rejected' ? 'pending' : u.status } : u));
      alert('Verification email sent.');
    } catch (err: any) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleUpdateRole = async (userId: string, newRole: 'admin' | 'user') => {
    try {
      await client.post(`/admin/update-role/${userId}`, { role: newRole });
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (err: any) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleSetSectionAccess = async (userId: string, section: PortalSection, enabled: boolean) => {
    try {
      const res = await client.post(`/admin/users/${userId}/section-access`, { section, enabled });
      setUsers(users.map(u => u.id === userId ? { ...u, sectionAccess: res.data.sectionAccess } : u));
    } catch (err: any) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');
    if (!addForm.name || !addForm.email || !addForm.firmId || !addForm.password) {
      setAddError('All fields are required.');
      return;
    }
    setAddLoading(true);
    try {
      await client.post('/admin/create-user', {
        name: addForm.name,
        email: addForm.email,
        password: addForm.password,
        firmId: addForm.firmId,
        role: addForm.role,
      });
      // Refresh user list
      const usersRes = await client.get('/admin/users');
      setUsers(usersRes.data.users || []);
      setShowAddUser(false);
      setAddForm({ name: '', email: '', firmId: '', role: 'user', password: '' });
      alert(`✓ ${addForm.name} added and approved. Send them a verification email via the @ button when ready.`);
    } catch (err: any) {
      setAddError(err.response?.data?.error || err.message || 'Failed to add user');
    } finally {
      setAddLoading(false);
    }
  };

  const stats = [
    { label: 'Total', value: users.length },
    { label: 'Pending Verification', value: users.filter(u => u.status === 'pending' && !u.emailVerified).length },
    { label: 'Ready for Approval', value: users.filter(u => u.status === 'pending' && u.emailVerified).length },
    { label: 'Approved', value: users.filter(u => u.status === 'approved').length },
    { label: 'Rejected', value: users.filter(u => u.status === 'rejected').length },
  ];

  const isSuperAdmin = user?.role === 'super_admin';

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--c-text-muted)' }}>Loading users…</div>;
  }

  if (error) {
    return (
      <div style={{ padding: 20, background: 'var(--c-error-bg)', border: '1px solid var(--c-error-border)', borderRadius: 8, color: 'var(--c-error)' }}>
        <strong>Error:</strong> {error}
      </div>
    );
  }

  return (
    <>
      {/* Hero Banner */}
      <div style={{
        background: 'var(--c-primary-10)',
        borderRadius: 'var(--r)',
        padding: '12px 16px',
        marginBottom: 20,
        borderLeft: '3px solid var(--c-accent)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--c-primary)' }}>User Management</div>
          <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 1 }}>Review and manage member registrations</div>
        </div>
      </div>

      {/* Stats */}
      <div className="admin-stats" style={{ marginBottom: 28 }}>
        {stats.map(stat => (
          <div key={stat.label} className="stat-card">
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by name, email or firm…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="form-input"
          style={{ flex: 1, minWidth: 200, maxWidth: 340 }}
        />
        {isSuperAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowAddUser(true)}>+ Add User</button>
        )}
      </div>

      {/* Status Filter Pills */}
      <div className="filter-bar" style={{ marginBottom: 10 }}>
        {['all', 'pending', 'approved', 'rejected'].map(s => (
          <button
            key={s}
            className={`filter-btn ${filterStatus === s ? 'active' : ''}`}
            onClick={() => setFilterStatus(s)}
          >
            {s === 'all' ? 'All' : s === 'pending' ? 'Pending Approval' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Firm Filter Pills */}
      <div className="filter-bar" style={{ marginBottom: 16, overflowX: 'auto', flexWrap: 'nowrap' }}>
        <button
          className={`filter-btn ${filterFirm === 'all' ? 'active' : ''}`}
          onClick={() => setFilterFirm('all')}
        >All Firms</button>
        {firmNames.map(f => (
          <button
            key={f}
            className={`filter-btn ${filterFirm === f ? 'active' : ''}`}
            onClick={() => setFilterFirm(f)}
          >{f}</button>
        ))}
      </div>

      {/* Data Table */}
      <div className="table-wrap">
        <table className="data-table" style={{ minWidth: 820 }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Firm</th>
              <th>Verified</th>
              <th>Role</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', color: 'var(--c-text-muted)', padding: 24 }}>
                  No users found
                </td>
              </tr>
            ) : (
              filteredUsers.map(u => (
                <tr key={u.id}>
                  <td style={{ fontSize: 13, fontWeight: 500, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</td>
                  <td style={{ fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</td>
                  <td style={{ fontSize: 12, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.firmName}</td>
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 600, color: u.emailVerified ? 'var(--c-primary)' : 'var(--c-warning)' }}>
                      {u.emailVerified ? '✓ Yes' : '✗ No'}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 600, color: u.role === 'super_admin' ? 'var(--c-primary)' : u.role === 'admin' ? 'var(--c-accent)' : 'var(--c-text-muted)' }}>
                      {u.role === 'super_admin' ? 'SA' : u.role ? u.role.charAt(0).toUpperCase() + u.role.slice(1) : 'User'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge badge-${u.status === 'pending' ? 'warning' : u.status === 'approved' ? 'accent' : 'error'}`} style={{ fontSize: 10 }}>
                      {u.status === 'pending' ? 'Pending' : u.status.charAt(0).toUpperCase() + u.status.slice(1)}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(u.createdAt)}</td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(u.lastLogin)}</td>
                  <td>
                    {isSuperAdmin && u.role !== 'super_admin' && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {/* Reset email */}
                        <button
                          title="Reset email verification & resend"
                          onClick={() => handleResetEmailVerification(u.id)}
                          style={actionBtn('var(--c-primary)')}
                        >@</button>

                        {/* Verify email + approve in one step (pending, unverified only) */}
                        {u.status === 'pending' && !u.emailVerified && (
                          <button
                            title="Verify email & approve (skips email verification)"
                            onClick={() => handleManualApprove(u.id)}
                            style={actionBtn('var(--c-warning)')}
                          >✓!</button>
                        )}

                        {/* Approve / Reject for pending+verified */}
                        {u.status === 'pending' && u.emailVerified && (<>
                          <button title="Approve" onClick={() => handleApprove(u.id)} style={actionBtn('var(--c-primary)')}>✓</button>
                          <button title="Reject" onClick={() => handleReject(u.id)} style={actionBtn('var(--c-error)')}>✗</button>
                        </>)}

                        {/* Role toggle for approved users */}
                        {u.status === 'approved' && (
                          <button
                            title={u.role === 'admin' ? 'Demote to User' : 'Promote to Admin'}
                            onClick={() => handleUpdateRole(u.id, u.role === 'admin' ? 'user' : 'admin')}
                            style={actionBtn('var(--c-accent)')}
                          >{u.role === 'admin' ? '↓' : '↑'}</button>
                        )}

                        {/* Section access permissions */}
                        {u.status === 'approved' && (
                          <button
                            title="Manage section access"
                            onClick={() => setAccessModalUserId(u.id)}
                            style={{ ...actionBtn('var(--c-primary)'), width: 'auto', padding: '0 8px' }}
                          >Access</button>
                        )}

                        {/* Revoke approved */}
                        {u.status === 'approved' && (
                          <button title="Revoke access" onClick={() => handleRevoke(u.id)} style={actionBtn('var(--c-error)')}>✗</button>
                        )}
                      </div>
                    )}
                    {!isSuperAdmin && u.role !== 'super_admin' && (
                      <div style={{ display: 'flex', gap: 3 }}>
                        <button title="Reset email verification" onClick={() => handleResetEmailVerification(u.id)} style={actionBtn('var(--c-primary)')}>@</button>
                        {u.status === 'approved' && (
                          <button title="Revoke access" onClick={() => handleRevoke(u.id)} style={actionBtn('var(--c-error)')}>✗</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Access Permissions Modal */}
      {accessModalUserId && (() => {
        const accessUser = users.find(u => u.id === accessModalUserId);
        if (!accessUser) return null;
        return (
          <div className="modal-overlay" onClick={() => setAccessModalUserId(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 style={{ margin: 0, fontSize: 16 }}>Section Access — {accessUser.name}</h3>
                <button className="modal-close" onClick={() => setAccessModalUserId(null)}>×</button>
              </div>
              <div className="modal-body">
                {SECTION_LABELS.map(({ section, label }) => {
                  const defaultValue = section === 'regulatoryUpdates' ? false : true;
                  const enabled = accessUser.role === 'super_admin'
                    ? true
                    : (accessUser.sectionAccess?.[section] ?? defaultValue);
                  return (
                    <label
                      key={section}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}
                    >
                      <input
                        type="checkbox"
                        checked={enabled}
                        disabled={accessUser.role === 'super_admin'}
                        onChange={(e) => handleSetSectionAccess(accessUser.id, section, e.target.checked)}
                      />
                      <span style={{ fontSize: 14 }}>{label}</span>
                    </label>
                  );
                })}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline btn-sm" onClick={() => setAccessModalUserId(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add User Modal */}
      {showAddUser && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }} onClick={() => setShowAddUser(false)}>
          <div style={{
            background: 'white', borderRadius: 'var(--r-lg)', padding: 32, width: '100%', maxWidth: 460,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 4 }}>Add User</h3>
            <p style={{ color: 'var(--c-text-muted)', fontSize: 14, marginBottom: 24 }}>
              User will be created and approved. Use the @ button to send their verification email when ready.
            </p>
            {addError && (
              <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--c-error-bg)', border: '1px solid var(--c-error-border)', borderRadius: 6, color: 'var(--c-error)', fontSize: 13 }}>
                {addError}
              </div>
            )}
            <form onSubmit={handleAddUser}>
              <div className="form-group">
                <label className="form-label">Full Name <span className="req">*</span></label>
                <input className="form-input" type="text" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} placeholder="Jane Smith" />
              </div>
              <div className="form-group">
                <label className="form-label">Email <span className="req">*</span></label>
                <input className="form-input" type="email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} placeholder="jane@firm.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Firm <span className="req">*</span></label>
                <select className="form-input" value={addForm.firmId} onChange={e => setAddForm(f => ({ ...f, firmId: e.target.value }))}>
                  <option value="">Select a firm…</option>
                  {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Role <span className="req">*</span></label>
                <select className="form-input" value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value as 'admin' | 'user' }))}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Temporary Password <span className="req">*</span></label>
                <input className="form-input" type="text" value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 8 chars, upper, lower, number, special" />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={addLoading}>
                  {addLoading ? 'Adding…' : 'Add User'}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => { setShowAddUser(false); setAddError(''); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

// Small icon-style action button helper
const actionBtn = (bg: string): React.CSSProperties => ({
  background: bg,
  color: 'white',
  border: 'none',
  borderRadius: 4,
  width: 28,
  height: 28,
  fontSize: 13,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  lineHeight: 1,
});
